import React, {
  useEffect, useState, useContext, useRef,
} from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import uuid from 'react-uuid';
import { browserHistory } from '@americanexpress/one-app-router';
import { useReactlytics } from '@americanexpress/reactlytics';
import moment from 'moment';
import { ChildContext } from './context';
import {
  getObjectLength,
  generateMagicNumber, getNewSearchString,
  getIndividualTableMetaData, addHeader, createFormForPostRedirection,
  createInputForPostRedirection, stringifyObjectValues, groupIds, encryptPayload,
} from '../utils/util';
import { constructChildStructure } from './ChildConstructor';
import {
  sequenceIDApi, validateStatusOnWhatsapp,
} from '../utils/utilService';
import { handleClicApiCall } from '../utils/clic';
import { checkDependentValidations } from '../Validations/Validations';
import { performOperations } from '../Operations/Operations';
import { apiHandler } from '../utils/apiHandler';
import EsignUtils from '../utils/esignUtils';
import useFormPayload from '../CustomHooks/useFormPayload';
import useValidation from '../CustomHooks/useValidation';
import useEntityData from '../CustomHooks/useEntityData';
import useReviewComments from '../CustomHooks/useReviewComments';
import useOverlayState from '../CustomHooks/useOverlayState';
import usePopUpState from '../CustomHooks/usePopUpState';
import useFileState from '../CustomHooks/useFileState';
import useMemberSelection from '../CustomHooks/useMemberSelection';
import useS3Operations from '../CustomHooks/useS3Operations';
import usePageNavigation from '../CustomHooks/usePageNavigation';
import { extractMapResponseValueByKey } from '../utils/conditionEvaluator';
import { iterateChildElements } from '../utils/dynamicPropertyEvaluator';

const getVal = require('lodash/get');

/**
 * element component receives json data
 * and pass futher for generation element
 * @param {formContent} param1
 */
const Element = ({
  formContent,
  loggerApiCall,
  journeyId,
  urlParams,
}) => {
  const formPageType = formContent
    && formContent.formContainer ? formContent.formContainer.pageType : null;
  const submitApiUrls = useSelector((state) => state.getIn(['config', 'submitApiUrls']));
  const iformIntegrationUrls = useSelector((state) => state.getIn(['config', 'memberApiUrls']));
  const iTrackerApiBaseUrls = useSelector((state) => state.getIn(['config', 'iTrackerApiBaseUrls']));
  const {
    comments, setComments,
    reviewComments, setReviewComments,
    entityComments, setEntityComments,
  } = useReviewComments();
  const [s3PayloadOtherInfo, setS3PayloadOtherInfo] = useState({});
  const appBaseUrls = useSelector((state) => state.getIn(['config', 'appBaseUrls']));
  const contextVal = useContext(ChildContext);
  const {
    renderForm, setRenderForm,
    enableElems, setEnableElems,
    renderingEl, setRenderingEl,
    isFormSubmission, setIsFormSubmission,
    isSubmitButtonDisable, setIsSubmitButtonDisable,
    disabledButtons, setDisabledButtons,
    apiResponse, setApiResponse,
  } = useOverlayState();
  const {
    showPopUp, setShowPopUp,
    evaluatePopUpId, setEvaluatePopUpId,
    evaluatePopUpTrigger,
  } = usePopUpState();
  const { formContainer, elementContainer } = formContent || {};
  const { cssProperties } = formContainer || {};
  const {
    formSubmissionPayload,
    setFormSubmissionPayload,
    curPageValues,
    setCurPageValues,
    changePageValues,
    setChangePageValues,
    refreshFormSubmissionPayload,
  } = useFormPayload(urlParams);
  const [userConsentCheckboxValue, setUserConsentCheckboxValue] = useState(false);
  const [currencySymbol, setCurrencySymbol] = useState('');
  const bgImage = cssProperties.bgImage ? cssProperties.bgImage : '';
  const bgImageSize = cssProperties.bgImageSize ? cssProperties.bgImageSize : '';
  const { track } = useReactlytics();
  const [dynamicClicDetails, setDynamicClicDetails] = useState({});
  const { validationErrors, setValidationErrors, setFocus } = useValidation();
  const {
    entityData, setEntityData,
    entityToEdit, setEntityToEdit,
    entityId, setEntityId,
    entityCompositeKey, setEntityCompositeKey,
    idsToClear, dispatchIdsState,
  } = useEntityData();
  const {
    fileUploadInfo,
    filesToAttach, setFilesToAttach,
    s3FileInfo, setS3FileInfo,
    allS3FileIds, setAllS3FileIds,
    griffinFileInfo, setGriffinFileInfo,
    s3EsignFileInfo, setS3EsignFileInfo,
    fileData, setFileData,
    isFileDataFromInput,
    getFileUploadInfo,
    createFile,
    fileSubmit,
  } = useFileState();
  const {
    memberDetails,
    selectedMemberInfo, setSelectedMemberInfo,
    selectedSuppAccount,
    setSelectedMember,
    setSuppSelectedMember,
  } = useMemberSelection({ formSubmissionPayload, setFormSubmissionPayload });

  /* Creating object of functions to call dynamically via JSON */
  let objectOfFunctions = {
    formSubmissionPayload,
    entityData,
    s3FileInfo,
    allS3FileIds,
    griffinFileInfo,
    dynamicClicDetails,
    setFormSubmissionPayload,
    selectedMemberInfo,
    setSelectedMemberInfo,
    comments,
    reviewComments,
    selectedSuppAccount,
    curPageValues,
    s3PayloadOtherInfo,
    entityComments,
    entityToEdit,
    memberDetails,
  };

  useEffect(() => {
    if (userConsentCheckboxValue) setIsSubmitButtonDisable(false);
    else setIsSubmitButtonDisable(true);
  }, [userConsentCheckboxValue]);

  const scrollTop = () => {
    window.scrollTo(0, 0);
  };

  const operationCallerRef = useRef(null);

  const currentDate = new Date();

  const payloadFields = {
    firstName:
      selectedMemberInfo && selectedMemberInfo.firstName
        ? selectedMemberInfo.firstName
        : '',
    lastName:
      selectedMemberInfo && selectedMemberInfo.lastName
        ? selectedMemberInfo.lastName
        : '',
    fullName:
      selectedMemberInfo && selectedMemberInfo.fullName
        ? selectedMemberInfo.fullName
        : '',
    accountToken: selectedMemberInfo && selectedMemberInfo.accountToken,
    suppaccountToken: selectedSuppAccount && selectedSuppAccount.accountToken,
    fileNames: fileUploadInfo.toString(),
    currencySymbol,
    timeStamp: currentDate[Symbol.toPrimitive]('string'),
    numberofAttachments: fileUploadInfo.length,
  };

  const payload = {
    head: {
      traceId: uuid(),
      span: 'iForm6513',
      journeyId,
    },
    body: {
      ...payloadFields,
      ...formSubmissionPayload,
    },
  };

  const handleMissingKeyToS3FileObjects = (payloadBody) => {
    const tempS3Details = JSON.parse(payloadBody.s3FileDetails);
    const keyIds = [];
    const updatedS3Details = [];
    const keyNotExistIndex = [];
    if (payloadBody?.entityData) {
      const tempEntityData = JSON.parse(payloadBody.entityData);
      formContainer.operations.addDataToS3.addS3ObjectKeysToOtherObjects.forEach((keyObj) => {
        const keyValues = [];
        // Iterate over s3FileInfo to segregate objects based on the presence of keyToAdd
        tempEntityData?.legalEntity.forEach((entityObj) => {
          if (entityObj[keyObj.keyToCheck]
            && keyValues.indexOf(entityObj[keyObj.keyToCheck]) === -1) {
            keyValues.push(entityObj[keyObj.keyToCheck]);
            keyIds.push({
              keyToAdd: keyObj.keyToAdd,
              value: entityObj[keyObj.keyToCheck],
            }); // Collect keyIds if keyToAdd exists
          }
        });
      });
    }

    if (keyIds.length === 0) {
      return payloadBody;
    }

    // Add missing keyToAdd to objects and push them back into tempS3FileInfo
    keyIds.forEach((keyId) => {
      tempS3Details.forEach((fileObj, index) => {
        if (fileObj[keyId.keyToAdd] === undefined) {
          updatedS3Details.push({ ...fileObj, [keyId.keyToAdd]: keyId.value });
        } else if (keyNotExistIndex.indexOf(index) === -1) {
          keyNotExistIndex.push(index);
          updatedS3Details.push(fileObj);
        }
      });
    });

    payloadBody.s3FileDetails = JSON.stringify(updatedS3Details);

    // Return the updated array
    return payloadBody;
  };

  const formatDate = (dateStr, inputDateFormat, outputDateFormat) => {
    const formatParts = inputDateFormat.match(/(YYYY|MM|DD)/g);
    const valueParts = dateStr.match(/\d+/g);
    if (!formatParts || !valueParts
      || formatParts.length !== valueParts.length
    ) {
      return 'Invalid format or input date';
    }
    const dateMap = {};
    formatParts.forEach((part, index) => {
      dateMap[part] = valueParts[index];
    });

    return outputDateFormat.replace(/YYYY|MM|DD/g, (match) => dateMap[match]);
  };
  const dateFormatChange = (
    operation,
    body
  ) => {
    if (operation?.dateFields) {
      operation.dateFields.forEach((dateField) => {
        if (dateField?.inputDateFormat && dateField?.outputDateFormat) {
          if (body[dateField.field]) {
            body[dateField.field] = formatDate(body[dateField.field],
              dateField?.inputDateFormat,
              dateField?.outputDateFormat);
          }
        }
      });
    }
  };
  const spreadEntityDataAndAddToPayload = (
    operation,
    functionObj
  ) => {
    const spreadPayload = {};
    const ObjToSpread = functionObj.entityData;
    const objGroupCount = new Map();

    if (operation?.tables) {
      operation.tables.forEach((table) => {
        if (table?.tableId && ObjToSpread[table.tableId]) {
          ObjToSpread[table.tableId].forEach((obj) => {
            if (table?.dateFields
              && table?.dateFields.length > 0) {
              if (table?.inputDateFormat && table?.outputDateFormat) {
                table?.dateFields.forEach((field) => {
                  if (obj[field]) {
                    obj[field] = formatDate(obj[field],
                      table?.inputDateFormat,
                      table?.outputDateFormat);
                  }
                });
              }
            }
            let suffixArrays = [''];
            if (table.suffixKey) {
              suffixArrays = obj[table.suffixKey].split(',')
                .map((val) => val.trim().replaceAll(/\s+/g, '_'));
            }
            suffixArrays.forEach((suffix) => {
              const countKey = `${table.tableId}_${suffix}`;

              if (objGroupCount.has(countKey)) {
                objGroupCount.set(countKey, objGroupCount.get(countKey) + 1);
              } else {
                objGroupCount.set(countKey, 1);
              }
              const objGroupIndex = objGroupCount.get(countKey);

              if (!table?.maxSuffixBase
                || (table?.maxSuffixBase
                  && objGroupIndex <= table?.maxSuffixBase
                )
              ) {
                if (suffix !== '') {
                  suffix = `_${suffix}`;
                }
                Object.keys(obj).forEach((key) => {
                  spreadPayload[`${key}_${objGroupIndex}${suffix}`] = obj[key];
                });
              }
            });
          });
        }
      });
    }
    return spreadPayload;
  };

  /**
   * form submission api call to send all form data
   */
  /* eslint complexity: ["error", 80] -- need to increase complexity */
  const submitApiCall = async () => {
    const formData = new FormData();

    // Attach fileData from Esign pdf/ Camera File/ Camera Video File to payload
    if (fileData) {
      formData.append('file', fileData);
    }
    // Attach files to payload without uploading to griffin
    if (filesToAttach && filesToAttach.length > 0) {
      filesToAttach.forEach((fileObj) => {
        formData.append('file', fileObj.data);
      });
    }
    // In case the body contains singpassPersonData DATA stringify it as it is json object
    if (payload.body && payload.body.singpassPersonData) {
      payload.body.singpassPersonData = JSON.stringify(payload.body.singpassPersonData);
    }
    /**
     * Add documentId array to payload
     */
    if (griffinFileInfo && griffinFileInfo.length > 0) {
      const documentIds = griffinFileInfo.reduce((acc, file) => {
        const docId = file.documentId;
        if (!acc.includes(docId)) {
          acc.push(docId);
        }
        return acc;
      }, []);
      payload.body.documentId = JSON.stringify(documentIds);
    }
    /* eslint-disable max-len -- need */
    /* eslint-disable no-restricted-syntax -- need */
    // In case we have to stringify some keys inside the payload
    // add a case to add the key in payload and then stringify that
    if (formContainer && formContainer.operations && formContainer.operations.onFormSubmit) {
      const formSubmissionOperations = formContainer.operations.onFormSubmit;
      const functionObj = { ...objectOfFunctions };
      if (formSubmissionOperations.length > 0) {
        for (const operation of formSubmissionOperations) {
          // const operation = formSubmissionOperations[i];
          switch (operation.type) {
            case 'groupIds': {
              const groups = groupIds(operation.groups, { formSubmissionPayload });
              Object.keys(groups).forEach((key) => {
                payload.body[key] = groups[key];
                formSubmissionPayload[key] = groups[key];
              });
              break;
            }
            case 'createKeysFromValue': {
              const keys = operation.keysToCreate;
              const value = getVal(functionObj, operation.value);
              const splitValues = value?.split(operation.separtor);
              if (splitValues?.length > 0) {
                splitValues.forEach((val, index) => {
                  payload.body[keys[index]] = val;
                  formSubmissionPayload[keys[index]] = val;
                });
              }
            }
              break;
            case 'stringifyDataKey':
              if (payload.body && payload.body[operation.keyToStringify]) {
                payload.body[operation.keyToStringify] = JSON.stringify(payload.body[operation.keyToStringify]);
              }
              break;
            case 'addKeyAndStringify':
              payload.body[operation.keyToAddAndStringify] = JSON.stringify(functionObj[operation.keyToAddAndStringify]);
              break;
            case 'spreadEntityDataAndAddToPayload': {
              const spreadPayload = spreadEntityDataAndAddToPayload(
                operation,
                functionObj
              );
              payload.body = {
                ...payload.body,
                ...spreadPayload,
              };
              break;
            }
            case 'dateFormatChange': {
              dateFormatChange(
                operation,
                payload.body
              );
              break;
            }
            case 'spreadAndAddToPayload':
              payload.body = {
                ...payload.body,
                ...functionObj[operation.keyToSpread],
              };
              break;
            case 'deleteFromPayload':
              delete payload.body[operation.keytoDelete];
              break;
            case 'fetchSequenceNumber': {
              /* eslint-disable no-await-in-loop -- need */
              const sequenceNum = await sequenceIDApi(iformIntegrationUrls, journeyId, contextVal.cuid, operation.format);
              payload.body[operation.addAs] = sequenceNum;
              formSubmissionPayload[operation.addAs] = sequenceNum;
              /* eslint-enable no-await-in-loop -- need */
              break;
            }
            case 'magicNumberGeneration': {
              const sequenceNum = generateMagicNumber(operation.format);
              payload.body[operation.addAs] = sequenceNum;
              formSubmissionPayload[operation.addAs] = sequenceNum;
              break;
            }
            case 'lastfiveDigitsToPayload': {
              const suppcardValues = formSubmissionPayload[operation.selectKey];
              const valueToAdd = suppcardValues.map((i) => i.displayNumber.slice(-5));
              payload.body[operation.addAs] = valueToAdd.join(',');
              formSubmissionPayload[operation.addAs] = valueToAdd.join(',');
              break;
            }
            case 'combineFields':
              {
                let newField = '';
                let fieldsToCombine = operation.fieldsToCombine || [];
                if (operation.prefix) {
                  for (const key in functionObj[operation.fieldFrom]) {
                    if (key.startsWith(operation.prefix)) {
                      fieldsToCombine = [...fieldsToCombine, {
                        fieldId: key,
                        fieldFrom: operation.fieldFrom,
                      }];
                    }
                  }
                }
                fieldsToCombine.forEach((fieldObj, index) => {
                  let val = '';
                  // get the fieldIds from formSubmissionPayload ? objectOfFunction[selectKey]
                  if (fieldObj.fieldFrom === 'String') {
                    val = fieldObj.fieldId;
                  } else {
                    val = getVal(functionObj[fieldObj.fieldFrom], fieldObj.fieldId);
                  }
                  if ((val !== undefined) && (val !== null) && val.constructor !== String) {
                    val = val.toString();
                  }
                  val = val && val !== '' ? val.trim() : null;
                  if (val) {
                    newField += (index > 0) && (newField !== '') ? `${operation.combineChar}${val}` : `${val}`;
                  }
                });
                if (newField !== '') {
                  payload.body[operation.addAs] = newField.trim();
                  formSubmissionPayload[operation.addAs] = newField.trim();
                }
              }
              break;
            case 'localDate':
              {
                const date = moment();
                const dateStr = date.format(operation.format);
                payload.body[operation.addAs] = dateStr;
                formSubmissionPayload[operation.addAs] = dateStr;
              }
              break;
            case 'createURL':
              {
                let url = '';
                operation.conditions.forEach((condition) => {
                  switch (formSubmissionPayload[operation.param]) {
                    case condition.value: {
                      url = condition.urlTemplate;
                      const matches = [...url.matchAll('\\~(.*?)\\~')];
                      for (const match in matches) {
                        if (formSubmissionPayload[matches[match][1]]) {
                          url = url.replaceAll(matches[match][0], formSubmissionPayload[matches[match][1]]);
                        }
                      }
                    }
                      break;
                    default: break;
                  }
                });
                payload.body[operation.addAs] = url;
                formSubmissionPayload[operation.addAs] = url;
              }
              break;
            case 'createS3FilesMetaData': {
              const s3FilesMetaData = [];
              const tempS3Info = s3FileInfo;
              /**
               * Creating separate metadata object for each file uploaded to S3
               * Files from same table are added as separate metadata object
               * Metadata is created from same fileObject
               */
              if (operation.allCombinedS3MetaData) {
                if (tempS3Info.length > 0) {
                  tempS3Info.forEach((fileObj) => {
                    // keysFromFileObj
                    const currFileMetadata = {
                      fileIds: [fileObj.fileId],
                    };
                    const keysToAddObject = operation.allCombinedS3MetaData.keysToAddFromFileObject;
                    Object.keys(keysToAddObject).forEach((key) => {
                      currFileMetadata[key] = fileObj[keysToAddObject[key]];
                    });
                    s3FilesMetaData.push(currFileMetadata);
                  });
                }
              }
              /**
               * Creating separate metadata object for file if files are from different tables
               * Files from same table will have have same metadata
               * Metadata can be created from dataObjects which are available. e.g formSubmissionPayload, etc.
               */
              if (operation.individualTables && operation.individualTables.length > 0) {
                operation.individualTables.forEach((table) => {
                  const currentTableFilesMetaData = getIndividualTableMetaData({
                    tableId: table.tableId,
                    dataKeysObj: table.associatedMetaData,
                    fileKeysObj: table.associatedMetaDataFromFileObj || null,
                    dataObj: functionObj[table.dataFrom],
                    fileInfoObj: tempS3Info,
                  }
                  );
                  // Add this table to s3FilesMetaData
                  if (currentTableFilesMetaData.length > 0) {
                    // push every file metadata object from crrentTableFilesMetaData to s3FilesMetaData
                    currentTableFilesMetaData.forEach((obj) => {
                      s3FilesMetaData.push(obj);
                    });
                  }
                });
              }
              // Entity Tables Metadata
              if (operation.entityTables) {
                Object.keys(operation.entityTables).forEach((entityName) => {
                  const currentEntity = entityData[entityName];
                  if (currentEntity && currentEntity.length > 0) {
                    currentEntity.forEach((entity) => {
                      // each entity can have multiple table
                      operation.entityTables[entityName].forEach((table) => {
                        const currentTableFilesMetaData = getIndividualTableMetaData({
                          tableId: table.tableId,
                          dataKeysObj: table.associatedMetaData,
                          dataObj: entity,
                          fileInfoObj: entity[table.tableId],
                        }
                        );
                        // Add this table to s3FilesMetaData
                        if (currentTableFilesMetaData.length > 0) {
                          // push every file metadata object from crrentTableFilesMetaData to s3FilesMetaData
                          currentTableFilesMetaData.forEach((obj) => {
                            s3FilesMetaData.push(obj);
                          });
                        }
                      });
                    });
                  }
                });
              }

              if (s3FilesMetaData.length > 0) {
                payload.body.s3FileDetails = JSON.stringify(s3FilesMetaData);
              }
              break;
            }
            case 'addMultipleValuesAsSingle':
            {
              let val = '';
              operation.multipleFields.forEach((fieldObj) => {
                val = getVal(functionObj[fieldObj.fieldFrom], fieldObj.fieldId);
                if (val) {
                  payload.body[operation.addAs] = val;
                  formSubmissionPayload[operation.addAs] = val;
                }
              });
              break;
            }
            case 'addGriffinDataToPayload': {
              payload.body[operation.addAs] = [...griffinFileInfo];
              break;
            }
            default: return true;
          }
        }
      }
    }
    /* eslint-enable no-restricted-syntax -- need */
    /* eslint-enable max-len -- need */
    // add esign file to original s3 array
    if (s3EsignFileInfo.length > 0) {
      if (payload.body.s3FileDetails) {
        payload.body.s3FileDetails = JSON.parse(payload.body.s3FileDetails);
      } else {
        payload.body.s3FileDetails = [];
      }
      payload.body.s3FileDetails.push(s3EsignFileInfo[0]);
      payload.body.s3FileDetails = JSON.stringify(payload.body.s3FileDetails);
    }

    // Add key to other s3FileDetails object
    const addS3ObjectKeysToOtherObjects = formContainer?.operations
      ?.addDataToS3?.addS3ObjectKeysToOtherObjects;
    if (addS3ObjectKeysToOtherObjects && payload.body.s3FileDetails) {
      payload.body = handleMissingKeyToS3FileObjects(payload.body);
    }
    if (s3FileInfo.length > 0 && s3FileInfo[0]?.fileType) {
      payload.body.fileType = s3FileInfo[0].fileType;
    }

    payload.body = stringifyObjectValues(payload.body);
    formData.append('data', encryptPayload(payload));
    const requestOptions = {
      method: 'POST',
      credentials: 'include',
      headers: {
        traceId: payload.head.traceId,
        journeyId,
      },
      body: formData,
    };
    let submitApi = `${submitApiUrls}/iForms/${formContainer.pageType}/iforms.do?cuid=${contextVal.cuid}`;
    if (formContainer
      && formContainer.formSubmissionQueryParams
      && formContainer.formSubmissionQueryParams.length > 0
    ) {
      // add these params in submit api
      formContainer.formSubmissionQueryParams.forEach((queryParam) => {
        const key = queryParam;
        const val = encodeURIComponent(formSubmissionPayload[queryParam]);
        submitApi = `${submitApi}&${key}=${val}`;
      });
    }
    try {
      loggerApiCall('status=calledFormSubmissionApi', 'info');
      const response = await fetch(`${submitApi}`, requestOptions);
      // if response.okay --> then only return response.json
      // else return null and log call failed
      if (response.status >= 200 && response.status < 300) {
        const result = await response.json();
        return { result, status: response.status };
      }
      return { result: null, status: response.status };
    } catch (error) {
      loggerApiCall(`network exception ${error}`, 'error');
      return { result: null, status: 500 };
    }
  };

  /**
   * On change api Call
   * Parameters
   * apiUrl: string
   */
  const callApiOnChange = async (apiUrl, targetVal = null) => {
    let response;
    const getCallRequestOptions = {
      defer: true,
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        traceId: uuid(),
        journeyId,
      },
    };
    if (apiUrl === 'getMemberEmail') {
      response = await fetch(
        `${iformIntegrationUrls}/iForms/secure/member/${selectedMemberInfo.accountToken}/email?cuid=${contextVal.cuid}`,
        getCallRequestOptions
      );
    } else if (apiUrl === 'getEmployeeDetails') {
      response = await fetch(
        `${submitApiUrls}/iForms/${formContainer.pageType}/employee?cuid=${contextVal.cuid}&name=${targetVal}`,
        getCallRequestOptions
      );
    } else if (apiUrl === 'getPayId') {
      response = await fetch(
        `${iformIntegrationUrls}/iForms/${formContainer.pageType}/payID/${selectedMemberInfo.accountToken}/enquire?cuid=${contextVal.cuid}`,
        getCallRequestOptions
      );
    } else {
      const requestOptions = {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: encryptPayload(payload),
      };
      response = await fetch(
        `${iformIntegrationUrls}/${apiUrl}?cuid=${contextVal.cuid}`,
        requestOptions
      );
    }
    return response;
  };

  /**
   * dynamic evaluate property from the container and passed to iterateChildElements
   */
  /* eslint-disable max-len -- need */
  const dynamicPropertyEvaluationForContainer = (container) => {
    const currentPayload = { ...formSubmissionPayload };
    const currentEntityPayload = { ...entityData };
    const currentFiles = [...filesToAttach];
    const currentS3Files = [...s3FileInfo];
    const currentDisabledButtons = { ...disabledButtons };
    container.elements.forEach((element, index) => {
      container.elements[index] = iterateChildElements(
        element, currentPayload, currentFiles, currentDisabledButtons, currentS3Files, currentEntityPayload, objectOfFunctions, formContainer
      );
    });
    if (getObjectLength(currentPayload) !== getObjectLength(formSubmissionPayload)) {
      setFormSubmissionPayload({ ...currentPayload, elemId: null });
    }
    if (Object.keys(currentEntityPayload).length !== Object.keys(entityData).length) {
      setEntityData(currentEntityPayload);
      setFormSubmissionPayload({ ...formSubmissionPayload, showEntityFields: 'Yes', elemId: null });
    }
    if (currentFiles.length !== filesToAttach.length) {
      setFilesToAttach([...currentFiles]);
    }
    if (currentS3Files.length !== s3FileInfo.length) {
      setS3FileInfo([...currentS3Files]);
    }
    if (getObjectLength(currentDisabledButtons) !== getObjectLength(disabledButtons)) {
      setDisabledButtons({ ...currentDisabledButtons });
    }
    return container;
  };
  /* eslint-enable max-len -- need */

  /**
  * Log page details and set html attributes on first render
  * update browser history on first render
  */
  useEffect(() => {
    let pageValue = '';
    if (formContainer.pageLabel) {
      document.title = formContainer.pageLabel;
    }
    if (formContainer.setFirstMember) {
      const firstMemberAccKey = memberDetails[0].identifiers.accountKey;
      setSelectedMember(firstMemberAccKey);
    }

    // evaluting first page number dynamically based on property key
    let firstPage;
    if (formContainer.operations && formContainer.operations.firstPageDynamicEvaluation) {
      firstPage = extractMapResponseValueByKey(
        formContainer.operations,
        'firstPageDynamicEvaluation',
        objectOfFunctions,
        formContainer
      );
    }

    // document.documentElement.setAttribute('lang', localeLang);
    elementContainer.forEach((container) => {
      if (container.page) {
        if (firstPage) {
          if (firstPage !== container.page) {
            container.isPageVisited = false;
            return;
          }
          container.isPageVisited = true;
        }

        if (container.isPageVisited) {
          const logString = `page=${container.page}`;
          loggerApiCall(logString, 'info');
          if (container.landingFormLastPage) {
            const logString1 = 'page=thankyou status=landingFormEnd';
            loggerApiCall(logString1, 'info');
          }
          const searchString = getNewSearchString(container.page);
          const updatedUrl = `${window.location.pathname}${searchString}`;
          browserHistory.push(updatedUrl);
          /* page tracking landing page */
          if (container.trackingName) {
            pageValue = container.trackingName;
          } else {
            pageValue = `page ${container.page}`;
          }
        }
      }
    });
    // biocatch context update
    if (contextVal?.cuid === 'TwoStageFormPlatinumCard_en_IN' && window?.cdApi) {
      window.cdApi.changeContext(`page:${pageValue}`);
    }

    track({
      event: {
        element: `On ${pageValue} form load`,
        detail: `${pageValue} visited`,
        type: 'impression',
        identifier: 'iForms',
        pageName: `${pageValue}`,
        meta: {
          page: {
            pageInfo: {
              pageName: `${pageValue}`,
            },
          },
        },
      },
    });
  }, []);

  /**
   * setting JSON data with component state
   * for rendering elements
   */
  useEffect(() => {
    elementContainer.forEach((container) => {
      if (container.page && container.isPageVisited) {
        const updatedContainer = dynamicPropertyEvaluationForContainer(container);
        const searchString = getNewSearchString(updatedContainer.page);
        const updatedUrl = `${window.location.pathname}${searchString}`;
        setRenderingEl(updatedContainer);
        browserHistory.push(updatedUrl);
        if (container.bioCatchKey) {
          window.setPageContext(container.bioCatchKey);
        }
      }
    });
  }, [elementContainer, formSubmissionPayload, selectedMemberInfo]);

  /**
   * Enable element when form is render
   */
  useEffect(() => {
    setEnableElems(!enableElems);
  }, [renderForm]);

  const { handleContinue, getContainerIndex } = usePageNavigation({
    elementContainer,
    formContainer,
    objectOfFunctions,
    loggerApiCall,
    setCurPageValues,
    setRenderingEl,
    dynamicPropertyEvaluationForContainer,
    track,
    scrollTop,
    operationCallerRef,
  });

  useEffect(() => {
    if (apiResponse?.response === 'error') {
      handleContinue('error');
    }
  }, [apiResponse]);

  /**
   * for handling back button functionlaity
   * @param {*} formDataObj
   */
  const handleBack = () => {
    if (renderingEl.previousPage) {
      handleContinue(renderingEl.previousPage);
    } else {
      browserHistory.goBack();
    }
  };
  const handlePrint = () => {
    window.print();
  };

  /**
 * Display success or error modal
 * If no modal then display the thank you page
 */
  const setModalVisible = (respType) => {
    const pageElements = renderingEl;
    const thankYouPageIndex = getContainerIndex('thankyou');
    const errorPageIndex = getContainerIndex('error');
    // log the status of submit api call

    if ((thankYouPageIndex !== -1) && (errorPageIndex !== -1)) {
      if (respType === 'T') {
        handleContinue('thankyou', 'submitButton');
      } else {
        handleContinue('error', 'submitButton');
      }
    } else if ((thankYouPageIndex === -1) && (errorPageIndex === -1)) {
      let pageValue = '';
      pageElements.elements.forEach((element) => {
        /**
         * Not making hidden elements visible
         * Making only modals visible
         */
        if (element && element.childElements.length > 0) {
          element.childElements.forEach((childEl) => {
            if (childEl.fieldIndicator === false) {
              if (
                childEl.fieldId === 'successModal'
                || childEl.fieldId === 'errorModal'
              ) {
                childEl.fieldIndicator = !childEl.fieldIndicator;
              }
            } else {
              childEl.fieldIndicator = !childEl.fieldIndicator;
            }
            if (respType === 'T' && childEl.fieldId === 'errorModal') {
              childEl.fieldIndicator = false;
              if (childEl.trackingName) {
                pageValue = childEl.trackingName;
              } else {
                pageValue = 'page thankyou';
              }
            } else if (respType === 'F' && childEl.fieldId === 'successModal') {
              const logString = 'page=error';
              loggerApiCall(logString, 'info');
              childEl.fieldIndicator = false;
              const searchString = getNewSearchString('error');
              const updatedUrl = `${window.location.pathname}${searchString}`;
              browserHistory.push(updatedUrl);

              if (childEl.trackingName) {
                pageValue = childEl.trackingName;
              } else {
                pageValue = 'page error';
              }
            }
          });
        }
      });
      // updating broswer histroy based on repsonse type
      if (respType === 'T') {
        const finalPage = respType === 'T' ? 'thankyou' : 'error';
        const logString = `page=${finalPage}`;
        loggerApiCall(logString, 'info');
        const searchString = getNewSearchString(finalPage);
        const updatedUrl = `${window.location.pathname}${searchString}`;
        browserHistory.push(updatedUrl);
      }
      /* page tracking for Modal page */
      track({
        event: {
          element: `On ${pageValue} form load`,
          detail: `${pageValue} visited`,
          type: 'impression',
          identifier: 'iForms',
          pageName: `${pageValue}`,
          meta: {
            page: {
              pageInfo: {
                pageName: `${pageValue}`,
              },
            },
          },
        },
      });
    } else {
      setRenderingEl(pageElements);
    }
    setEnableElems(!enableElems);
  };

  /**
   * for handling form submit event
   * @param {*} formDataObj
   */
  const handleSubmit = async () => {
    setIsFormSubmission(true);
    const logString = 'event=submitButtonClick';
    loggerApiCall(logString, 'info');
    const data = await submitApiCall();
    const { result, status } = data;
    if (result && result.statusIndicator) {
      let showThankyouPage = true;
      const triggerAfterSubmitOperations = (operations) => {
        operations.forEach((filter) => {
          switch (filter.type) {
            case 'addValueToPayload': {
              let resultObj = result.body || result;
              resultObj = { ...resultObj, status };
              const valueToAdd = getVal(resultObj, filter.selectKey);
              formSubmissionPayload[filter.addAs] = valueToAdd;
              break;
            }
            case 'redirect': {
              loggerApiCall('page=thankyou urlRedirected', 'info');
              showThankyouPage = false;
              window.location.href = formSubmissionPayload[filter.urlToRedirect];
              break;
            }
            default:
              break;
          }
        });
      };
      // log submit api call status
      if (result.statusIndicator === 'T') {
        const logString1 = 'status=formSubmissionApiCallSuccess';
        loggerApiCall(logString1, 'info');
        // we should perform this only if submission api successful?
        if (formContainer?.operations?.afterFormSubmit) {
          if (formContainer?.operations?.afterFormSubmit.length > 0) {
            triggerAfterSubmitOperations(formContainer.operations.afterFormSubmit);
          }
        }
      } else {
        const logString1 = `status=formSubmissionApiCallFailed errorMsg=${result.message}`;
        loggerApiCall(logString1, 'error');
        if (formContainer?.operations?.afterFormFailure) {
          if (formContainer?.operations?.afterFormFailure.length > 0) {
            triggerAfterSubmitOperations(formContainer.operations.afterFormFailure);
          }
        }
      }
      setIsFormSubmission(false);
      if (showThankyouPage) {
        setModalVisible(result.statusIndicator);
        scrollTop();
      }
    } else {
      setIsFormSubmission(false);
      const logString1 = `status=formSubmissionApiCallFailed errorMsg=networkCallFailedWithStatus${status}`;
      loggerApiCall(logString1, 'error');
      setModalVisible('F');
      scrollTop();
    }
    setCurPageValues({});
  };

  const dataObject = {
    memberInfo: selectedMemberInfo,
    formSubmissionPayload,
    journeyId,
    cuid: contextVal.cuid,
    iformIntegrationUrls,
    formContainer,
    entityData,
    entityId,
    s3FileInfo,
    renderingEl,
    idsToClear,
    entityToEdit,
    curPageValues,
  };

  let functionObject = {
    setFormSubmissionPayload,
    loggerApiCall,
    setIsFormSubmission,
    handleContinue,
    setEntityData,
    dispatchIdsState,
    setEntityToEdit,
  };

  const operationCaller = async (operations, additionalData = {}, additionalProps = {}) => {
    await performOperations({
      ...additionalProps,
      operations,
      dataObject: {
        ...dataObject,
        ...additionalData,
      },
      functionObject,
    });
  };
  operationCallerRef.current = operationCaller;

  useEffect(() => {
    if (formContainer.operations && formContainer.operations.onFormLoad) {
      operationCaller(formContainer.operations.onFormLoad);
    }
  }, []);

  const checkDependentFieldValidations = (onSubmit = false) => {
    if (onSubmit) {
      setFormSubmissionPayload((prevPayload) => ({
        ...prevPayload,
        setFirstOnSubmitFlag: true,
      }));
    }

    let errorIds = {};
    if (onSubmit || formSubmissionPayload.setFirstOnSubmitFlag) {
      errorIds = checkDependentValidations(renderingEl, formSubmissionPayload);

      if (Object.keys(errorIds).length > 0) {
        const hasDifferentValues = Object.keys(errorIds).some(
          (errorId) => formSubmissionPayload[errorId] !== errorIds[errorId]
        );

        if (hasDifferentValues) {
          setFormSubmissionPayload((prevPayload) => ({
            ...prevPayload,
            ...errorIds,
          }));
        }
      }
    }

    const falseErrorIds = Object.fromEntries(
      // eslint-disable-next-line no-unused-vars -- desc
      Object.entries(errorIds).filter(([_, value]) => !value)
    );

    return falseErrorIds;
  };

  useEffect(() => {
    checkDependentFieldValidations();
  }, [formSubmissionPayload]);

  const checkAnyKeysExistForUpdates = (dataset) => {
    let keysToUpdates = {};
    if (dataset && dataset?.keysToUpdate) {
      keysToUpdates = JSON.parse(dataset?.keysToUpdate);
    }
    return keysToUpdates;
  };

  /**
   *
   * @param {e} event provides formdata
   * switch case to handle different types of button click
   * and form submit
   */
  const onFormSubmit = (e) => {
    e.preventDefault();

    // Check multiple fields dependent validation
    const errorIds = checkDependentFieldValidations(true);

    if (!setFocus(validationErrors) && Object.keys(errorIds).length === 0) {
      const { id, dataset, name } = e.nativeEvent.submitter;
      const formData = new FormData(e.target);
      // delete pagewise entries from formSubmission payload for that page
      // addd new formDataObj to formSubmission payload
      const formDataObj = Object.fromEntries(formData.entries());
      switch (id) {
        case 'continue':
        case 'submit':
        case 'partialSubmit':
        case 'fetchS3Data':
        case 'saveDataToS3':
        case 'clic':
        case 'dynamicClic':
        case 'cardVerifySubmit':
        case 'showPopUp':
        case 'orixBtn':
        case 'legalDocContinue':
        case 'uboContinue':
        case 'showDefaultPopUp':
        case 'lookupData':
        case 'parallelApiCalls':
        case 'createClicCase':
        case 'cardVerify':
        case 'validateUserDetails':
        case 'stageOneSubmit':
        case 'resumeJourney':
        case 'ibanVerification':
        case 'c360Verification':
        case 'aceVerification':
        case 'triggerAPI': {
          // Store page wise fields values and remove older values from form submission
          const fSPayload = refreshFormSubmissionPayload(formDataObj, renderingEl.page);

          // Check any keys to be added to payload before continue/submit
          const keysToUpdates = checkAnyKeysExistForUpdates(dataset);

          // store every page form values for final form submission
          return setFormSubmissionPayload({
            ...fSPayload,
            ...formDataObj,
            ...{ elemId: id },
            ...keysToUpdates,
            callerId: name,
          });
        }
        case 'back':
          return handleBack();
        case 'addEntity': {
          // Check any keys to be added to payload before continue/submit
          const keysToUpdates = checkAnyKeysExistForUpdates(dataset);
          if (Object.keys(keysToUpdates).length > 0) {
            setFormSubmissionPayload({
              ...formSubmissionPayload,
              ...keysToUpdates,
            });
          }
          return operationCaller([{ type: 'addEntity' }], { dataObj: formDataObj });
        }
        case 'print':
          return handlePrint();
        default:
          setFormSubmissionPayload({ ...formSubmissionPayload, elemId: null });
          return null;
      }
    }
    return false;
  };

  /**
   * this function enables hidden elements if exists
   * and set previous page and current page flags accordingly
   * @param {*} arg
   */
  const refreshForm = (arg) => {
    /* eslint-disable-next-line max-len -- couldnt span comment in multiple lines */
    arg = Boolean(arg); // passing selectedValue as arg, if selectedValue="" => arg=false else arg=true
    setRenderForm(true);
    if (arg === true) {
      renderingEl.elements.forEach((element) => {
        if (element && element.childElements.length > 0) {
          element.childElements.forEach((childEl) => {
            if (!childEl.fieldDynamicDisplay) childEl.fieldIndicator = true;
            childEl.isFieldVisited = true;
          });
        }
      });
    } else {
      renderingEl.elements.forEach((element) => {
        if (element && element.childElements.length > 0) {
          element.childElements.forEach((childEl) => {
            if (!childEl.fieldRequired) childEl.fieldIndicator = false;
            childEl.isFieldVisited = false;
          });
        }
      });
    }
    setRenderingEl(renderingEl);
    setEnableElems(!enableElems);
  };

  const handleOrixApi = async () => {
    const logString = 'status=CallToFetchGenCode';
    loggerApiCall(logString, 'info');
    const genApiPayload = {
      head: {
        traceId: uuid(),
        span: 'span',
      },
      body: {
        accountToken: selectedMemberInfo !== null ? selectedMemberInfo.accountToken : null,
      },
    };

    setIsFormSubmission(true);
    // default --> channelCode=W seqId=99 --> these two may or maynot come from user url
    if (!formSubmissionPayload.channelCode) {
      formSubmissionPayload.channelCode = 'W';
    }
    if (!formSubmissionPayload.seqId) {
      formSubmissionPayload.seqId = '99';
    }
    fetch(`${iformIntegrationUrls}/iForms/${formContainer.pageType}/genEntity?cuid=${contextVal.cuid}&operation=toGEN`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: encryptPayload(genApiPayload),
    })
      .then((response) => response.json())
      .then((data) => {
        if (data && data.statusIndicator === 'T') {
          loggerApiCall('page=thankyou status=genEntityApiCallSuccess', 'info');
          // 7+channelCode+seqId+G+GENnumber
          const codeId = `7${formSubmissionPayload.channelCode}${formSubmissionPayload.seqId}G${data.message}`;
          const { redirectUrl } = formContainer.operations.postRedirection;
          addHeader('referrer', 'unsafe-url');
          createFormForPostRedirection('sbsOrix', redirectUrl);
          createInputForPostRedirection('amxId', codeId, 'sbsOrix');
          const form = document.querySelector('#sbsOrix');
          form.submit();
          document.body.removeChild(form);
        } else {
          setIsFormSubmission(false);
          loggerApiCall(`status=genEntityApiCallFailed errorMsg=${data.message}`, 'error');
          setFormSubmissionPayload((prev) => ({ ...prev, elemId: 'error', genEntityStatus: 0 }));
        }
      });
  };

  /**
   * clic api call
   */
  const handleClicApi = (searchUrlType) => {
    handleClicApiCall({
      formContainer,
      formSubmissionPayload,
      selectedMemberInfo,
      setFormSubmissionPayload,
      iformIntegrationUrls,
      contextVal,
      handleContinue,
      searchUrlType,
      setDynamicClicDetails,
      journeyId,
      loggerApiCall,
      setIsFormSubmission,
    });
  };

  /**
   * Reset objectOfFunctions for dynamically call functions
   */
  objectOfFunctions = {
    ...objectOfFunctions,
    setSelectedMember,
    callApiOnChange,
    refreshForm,
    setSuppSelectedMember,
  };

  const { getS3Data, putDataToS3 } = useS3Operations({
    formContainer,
    iformIntegrationUrls,
    cuid: contextVal.cuid,
    journeyId,
    appBaseUrls,
    loggerApiCall,
    formSubmissionPayload,
    setFormSubmissionPayload,
    setIsFormSubmission,
    entityData,
    setEntityData,
    allS3FileIds,
    setAllS3FileIds,
    s3FileInfo,
    setS3FileInfo,
    s3EsignFileInfo,
    setComments,
    setReviewComments,
    setEntityComments,
    setS3PayloadOtherInfo,
    objectOfFunctions,
    handleContinue,
  });

  /**
     * Update functionObject
     */
  functionObject = {
    ...functionObject,
    putDataToS3,
  };

  /**
   * Handle on change event of element, based on JSON following action performs
   * Call Rest APIs
   * Update states
   * @param {*} changeEvent: OnElementChange object
   */
  const handleOnChangeApiCall = async (changeEvent, targetVal = null) => {
    /** If any api call */
    let data = {};
    if (changeEvent.apiFunctionCall && changeEvent.apiFunctionCall !== '') {
      const logString = `status=called${changeEvent.fetchUrl}`;
      loggerApiCall(logString, 'info');
      const res = await objectOfFunctions[changeEvent.apiFunctionCall](
        changeEvent.fetchUrl, targetVal
      );
      if (res.ok) {
        data = await res.json();
        const logString1 = `status=apiCallSuccess-${changeEvent.fetchUrl}`;
        loggerApiCall(logString1, 'info');
      } else {
        data = changeEvent.defaultErrorMessage;
        const logString1 = `status=apiCallFailed-${changeEvent.fetchUrl} message=callFailedWithStatus${res.status}`;
        loggerApiCall(logString1, 'error');
      }
    }

    /** If any state update */
    /* eslint-disable no-restricted-syntax -- need */
    if (changeEvent.statesToUpdate && changeEvent.statesToUpdate.length > 0) {
      for (const statefuncObj of changeEvent.statesToUpdate) {
        let updateObj = { ...objectOfFunctions[statefuncObj.stateName] };
        const newObj = {};
        for (const updateValue of statefuncObj.updateData) {
          newObj[updateValue.key] = updateValue.valueToMap !== ''
            ? getVal(data, updateValue.valueToMap)
            : updateValue.defaultValue;
        }
        updateObj = { ...updateObj, ...newObj };
        objectOfFunctions[statefuncObj.funcName]({
          ...objectOfFunctions[statefuncObj.stateName],
          ...updateObj,
        });
      }
    }
    /* eslint-enable no-restricted-syntax -- need */
  };

  /**
   * Call validation api after selected member state update
   */
  useEffect(async () => {
    if (formContainer?.operations?.onCardChange) {
      if (selectedMemberInfo) {
        await operationCaller(formContainer.operations.onCardChange);
      }
    }
    if (renderingEl.elements) {
      renderingEl.elements.forEach((element) => {
        if (element && element.childElements.length > 0) {
          element.childElements.forEach((childEl) => {
            if (childEl.onElementChange) {
              // eslint-disable-next-line no-restricted-syntax -- need
              for (const changeEvent of childEl.onElementChange) {
                if (selectedMemberInfo && selectedMemberInfo.accountToken) {
                  if (changeEvent.isValidationApi) {
                    handleOnChangeApiCall(changeEvent);
                  } else if (changeEvent.updatePayload) {
                    let valuesToAdd = {};
                    changeEvent.valuesToUpdate.forEach((eventObj) => {
                      switch (eventObj.valueFrom) {
                        case 'selectedMemberInfo': {
                          const val = getVal(selectedMemberInfo, eventObj.value);
                          valuesToAdd = { ...valuesToAdd, ...{ [eventObj.id]: val } };
                          break;
                        }
                        default: return true;
                      }
                      return true;
                    });
                    setFormSubmissionPayload({ ...formSubmissionPayload, ...valuesToAdd });
                  }
                } else if (changeEvent.nullMemberInfoDeleteKeysFromPayload
                  && changeEvent.nullMemberInfoDeleteKeysFromPayload.length > 0) {
                  changeEvent.nullMemberInfoDeleteKeysFromPayload.forEach(
                    (key) => delete formSubmissionPayload[key]
                  );
                  setFormSubmissionPayload({ ...formSubmissionPayload, elemId: null });
                }
              }
            }
          });
        }
      });
    }
  }, [selectedMemberInfo]);

  const handleUserDetailsValidation = async () => {
    let validate = true;

    // validate Date of birth
    let dob = '';
    if (memberDetails[0].holder.profile.birthDate.day > 9) {
      dob += `${memberDetails[0].holder.profile.birthDate.day}/`;
    } else {
      dob += `0${memberDetails[0].holder.profile.birthDate.day}/`;
    }

    if (memberDetails[0].holder.profile.birthDate.month > 9) {
      dob += `${memberDetails[0].holder.profile.birthDate.month}/`;
    } else {
      dob += `0${memberDetails[0].holder.profile.birthDate.month}/`;
    }

    dob += memberDetails[0].holder.profile.birthDate.year;

    if (dob !== formSubmissionPayload.dateOfBirth) {
      validate = false;
    }

    // validate card last 4 digit
    memberDetails.every((member) => {
      const cardLast4Digit = member.identifiers.displayAccountNumber.slice(-4);
      if (cardLast4Digit !== formSubmissionPayload.card_last_4_digit) {
        validate = false;
      } else {
        return false;
      }
      return true;
    });

    loggerApiCall(`${contextVal.cuid} status is ${validate ? 'Passes' : 'Failed'}`, 'info');

    if (validate) {
      let countryCode = '';
      let phoneNumber = '';
      if (memberDetails[0].holder.contacts.phone.length > 0) {
        memberDetails[0].holder.contacts.phone.every((pNumber) => {
          if (pNumber.type.toLowerCase() === 'mobile') {
            countryCode = pNumber.countryCode;
            phoneNumber = pNumber.phoneNumber;
            return false;
          }
          return true;
        });
      }

      loggerApiCall(`${contextVal.cuid} calling the api`, 'info');
      try {
        // create Payload
        const paramObj = {
          iformIntegrationUrls,
          pageType: formContainer.pageType,
          cuid: contextVal.cuid,
          requestPayload: {
            head: {
              traceId: uuid(),
              span: 'iForm6513',
              journeyId,
            },
            body: {
              profileName: urlParams.profileName ? urlParams.profileName : 'N/A',
              source: urlParams.source ? urlParams.source : 'whatsapp',
              countryCode,
              phoneNo: phoneNumber,
              registrationToken: urlParams.registrationToken,
            },
          },
        };
        const response = await validateStatusOnWhatsapp(paramObj);
        loggerApiCall(`${contextVal.cuid} response ${response.status}`, 'info');
        if (response.status === 200) {
          handleContinue('success');
        } else {
          handleContinue('error');
        }
      } catch (e) {
        loggerApiCall(`${contextVal.cuid} error in api ${e.message}`, 'error');
        handleContinue('error');
      }
    } else {
      handleContinue('error');
    }
  };

  /**
   * Call when any changes to formSubmissionPayload
   */

  useEffect(async () => {
    if (formSubmissionPayload && formSubmissionPayload.elemId) {
      if (formSubmissionPayload.elemId === 'continue'
        || formSubmissionPayload.elemId === 'legalDocContinue'
        || formSubmissionPayload.elemId === 'uboContinue'
      ) {
        handleContinue();
      } else if (formSubmissionPayload.elemId === 'submit') {
        if (formSubmissionPayload && formSubmissionPayload.Documents_1 && (formSubmissionPayload.Documents_1 !== '')) {
          setFormSubmissionPayload({ ...formSubmissionPayload, ...{ Documents_1: '' } });
        } else {
          handleSubmit();
        }
      } else if (formSubmissionPayload.elemId === 'clic') {
        handleClicApi('search');
      } else if (formSubmissionPayload.elemId === 'dynamicClic') {
        apiHandler({
          dataObject: {
            formContainer,
            formSubmissionPayload,
            iTrackerApiBaseUrls,
            journeyId,
            cuid: contextVal.cuid,
            searchType: 'dynamicSearch',
          },
          functionObject: {
            loggerApiCall,
            handleContinue,
            handleClicApi,
          },
          apiName: 'fetchItrackerStatus',
          apiCallerId: formSubmissionPayload.elemId,
        });
      } else if (
        formSubmissionPayload.elemId === 'lookupData'
        || formSubmissionPayload.elemId === 'cardVerify'
        || formSubmissionPayload.elemId === 'cardVerifySubmit'
        || formSubmissionPayload.elemId === 'ibanVerification'
        || formSubmissionPayload.elemId === 'createClicCase'
        || formSubmissionPayload.elemId === 'stageOneSubmit'
        || formSubmissionPayload.elemId === 'resumeJourney'
        || formSubmissionPayload.elemId === 'c360Verification'
        || formSubmissionPayload.elemId === 'aceVerification'
        || formSubmissionPayload.elemId === 'triggerAPI'
      ) {
        setIsFormSubmission(true);
        await apiHandler({
          dataObject,
          functionObject,
          apiName: formSubmissionPayload.elemId,
          apiCallerId: formSubmissionPayload.callerId,
        });
        setIsFormSubmission(false);
      } else if (formSubmissionPayload.elemId === 'parallelApiCalls') {
        setIsFormSubmission(true);
        await apiHandler({
          dataObject,
          functionObject,
          ...formContainer.operations?.[formSubmissionPayload.callerId],
          callType: 'parallel',
        });
        setIsFormSubmission(false);
      } else if (formSubmissionPayload.elemId === 'fetchS3Data') {
        getS3Data();
      } else if (formSubmissionPayload.elemId === 'saveDataToS3') {
        putDataToS3();
      } else if (formSubmissionPayload.elemId === 'orixBtn') {
        handleOrixApi();
      } else if (formSubmissionPayload.elemId === 'validateUserDetails') {
        handleUserDetailsValidation();
      } else if (formSubmissionPayload.elemId === 'partialSubmit') {
        const isFormSubmissionRequired = formContainer?.operations
          ?.generateESignPdf?.isS3UploadRequiredForEsignPdf?.required;
        if (isFormSubmissionRequired && ['SIGNATURE', 'FORM_DOWNLOADED', 'HANKO', 'ESIGN'].indexOf(formSubmissionPayload?.status) === -1) {
          const esignObj = new EsignUtils(
            elementContainer,
            formContainer,
            formSubmissionPayload,
            s3FileInfo,
            setS3FileInfo,
            setAllS3FileIds,
            iformIntegrationUrls,
            contextVal,
            setS3EsignFileInfo,
            setIsFormSubmission,
            apiResponse,
            setApiResponse,
            loggerApiCall,
            objectOfFunctions,
            journeyId,
            payloadFields,
            setFileData,
            putDataToS3
          );
          esignObj.uploadEsignPdfToS3();
        } else {
          putDataToS3(true);
        }
      } else if (formSubmissionPayload.elemId === 'showPopUp' || formSubmissionPayload.elemId === 'showDefaultPopUp') {
        // pop-up will be shown based on conditions
        // decide which pop-up to trigger -> pass this pop-up id to evaluatePopUpTrigger
        const conditions = formSubmissionPayload.elemId === 'showPopUp' ? formContainer.operations.popUpTriggers[evaluatePopUpId].conditions : null;
        if (evaluatePopUpTrigger(conditions, formSubmissionPayload)) {
          setShowPopUp(evaluatePopUpId);
        } else if (formContainer.operations.popUpTriggers[evaluatePopUpId].navigate === 'submit') {
          handleSubmit();
        } else if (formContainer.operations.popUpTriggers[evaluatePopUpId].navigate === 'continue') {
          handleContinue();
        }
      } else if (formSubmissionPayload.elemId === 'error') {
        handleContinue('error');
      }
    }
  }, [formSubmissionPayload]);

  useEffect(() => {
    if (s3EsignFileInfo.length > 0) {
      if (formContainer.operations?.generateESignPdf
        ?.isS3UploadRequiredForEsignPdf?.callSubmitApi) {
        setFormSubmissionPayload({ ...formSubmissionPayload, ...{ elemId: 'submit' } });
      } else {
        // Update the data as well on S3
        putDataToS3(true);
      }
    }
  }, [s3EsignFileInfo]);

  useEffect(() => {
    if (fileData && !isFileDataFromInput) {
      setFormSubmissionPayload({ ...formSubmissionPayload, ...{ elemId: 'submit' } });
    }
  }, [fileData]);

  /**
   * Handle on change event of element, based on JSON following action performs
   * Call Rest APIs
   * Call functions
   * Update states
   * @param {*} onElementChange: Array of objects
   * @param {*} elementObj: element instance
   */
  /* eslint-disable no-restricted-syntax -- need */
  const handleOnChangeEvents = (onElementChange, elementObj, value = '') => {
    const selectedValue = elementObj !== '' ? elementObj.target.value : value;
    const id = elementObj !== '' ? elementObj.target.id : null;
    for (const changeEvent of onElementChange) {
      /** If any function call */
      if (
        changeEvent.functionsToCall
        && changeEvent.functionsToCall.length > 0
      ) {
        for (const functionObj of changeEvent.functionsToCall) {
          if (functionObj.parameters !== '') {
            if (value !== '') {
              objectOfFunctions[functionObj.name](functionObj.parameters === 'selectedValue' ? { feedbackRelatedTo: selectedValue } : functionObj.parameters);
            } else {
              /* eslint-disable no-lonely-if -- need */
              /* eslint-disable max-len -- need */
              if (functionObj.name === 'setFormSubmissionPayload') {
                setFormSubmissionPayload((prev) => ({ ...prev, ...{ [id]: selectedValue }, ...{ elemId: id } }));
              } else {
                objectOfFunctions[functionObj.name](functionObj.parameters === 'selectedValue' ? selectedValue : functionObj.parameters);
              }
            }
          } else {
            objectOfFunctions[functionObj.name]();
          }
        }
      }
      /* eslint-enable max-len -- need */
      /* eslint-enable no-lonely-if -- need */
      /** If any api call and state update */
      if (changeEvent.apiFunctionCall && !changeEvent.isValidationApi) {
        handleOnChangeApiCall(changeEvent, selectedValue);
      }
    }
    /* eslint-enable no-restricted-syntax -- need */
  };

  /* eslint-disable max-len -- need */
  const props = {
    refreshForm,
    formSubmissionPayload,
    selectedMemberInfo,
    setUserConsentCheckboxValue,
    handleSubmit,
    getFileUploadInfo: (fileInfo) => getFileUploadInfo(fileInfo, setIsSubmitButtonDisable),
    setCurrencySymbol,
    handleOnChangeEvents,
    createFile,
    fileSubmit: (file) => fileSubmit(file, setIsSubmitButtonDisable),
    fileUploadInfo,
    handleBack,
    isSubmitButtonDisable,
    setIsSubmitButtonDisable,
    loggerApiCall,
    journeyId,
    setFormSubmissionPayload,
    isFormSubmission,
    handleContinue,
    filesToAttach,
    setFilesToAttach,
    setValidationErrors,
    validationErrors,
    setFocus,
    entityData,
    setEntityData,
    idsToClear,
    entityToEdit,
    setEntityId,
    s3FileInfo,
    setS3FileInfo,
    allS3FileIds,
    setAllS3FileIds,
    formPageType,
    disabledButtons,
    setDisabledButtons,
    showPopUp,
    setShowPopUp,
    setEvaluatePopUpId,
    curPageValues,
    setCurPageValues,
    formContainer,
    griffinFileInfo,
    setGriffinFileInfo,
    comments,
    setComments,
    entityComments,
    setEntityComments,
    entityCompositeKey,
    setEntityCompositeKey,
    objectOfFunctions,
    extractMapResponseValueByKey: (jsonObject, propertykey, data = null) => extractMapResponseValueByKey(
      jsonObject, propertykey, data || objectOfFunctions, formContainer
    ),
    dispatchIdsState,
    putDataToS3,
    setIsFormSubmission,
    setS3EsignFileInfo,
    s3EsignFileInfo,
    payloadFields,
    setFileData,
    apiResponse,
    setApiResponse,
    elementContainer,
    changePageValues,
    setChangePageValues,
    iformIntegrationUrls,
    operationCaller,
    dataObject,
  };
    /* eslint-enable max-len -- need */
  // first checking for if it's a row, if not then column or accordion.
  return (
    <div
      className={cssProperties.style}
      style={{
        backgroundImage: `url(${bgImage})`,
        backgroundSize: bgImageSize,
      }}
    >
      <form onSubmit={onFormSubmit}>
        {renderingEl && renderingEl.elements
          ? constructChildStructure(renderingEl.elements, props, renderingEl.page)
          : null}
      </form>
    </div>
  );
};

Element.propTypes = {
  formContent: PropTypes.objectOf(PropTypes.shape),
  loggerApiCall: PropTypes.func,
  journeyId: PropTypes.string,
  urlParams: PropTypes.objectOf(PropTypes.shape),
};

export default Element;
